import { PayeeStatus } from "#graphql/supergraphEnums";
import { PaymentDocumentType, PayeeStatus as PayeeStatusType } from "#graphql/supergraphTypes";
import { PayeeDisplayStatusTypes } from "../../constants";
import { PayeeData } from "../../Context/PayeeContext";
import { PayeeVendorTaxDocument, TaxDocumentType } from "../../PayeeVendorWorkflow/types";
import { PayeeBaseSupportingDocument, PayeeBaseAddress } from "../../types";
import {
  GetPayeeQueryPayee,
  GetPayeeQueryDocument,
  GetPayeeQueryPaymentPrefDocument,
  GetPayeeQueryAddress,
} from "./types";

// Helper function to convert GraphQL PaymentDocumentType to TaxDocumentType
const convertToTaxDocumentType = (paymentDocType: PaymentDocumentType): TaxDocumentType => {
  switch (paymentDocType) {
    case "us_tax":
      return TaxDocumentType.US_TAX;
    case "non_us_tax":
      return TaxDocumentType.NON_US_TAX;
    default:
      // Default to US_TAX for other document types (shouldn't happen for tax documents)
      return TaxDocumentType.US_TAX;
  }
};

// Transform address from GraphQL format to PayeeBaseAddress format
const transformAddress = (address: GetPayeeQueryAddress): PayeeBaseAddress | undefined => {
  if (!address) return undefined;

  return {
    street: address?.street || undefined,
    street2: address?.street2 || undefined,
    city: address?.city || undefined,
    postalCode: address?.postalCode || undefined,
    stateId: address?.state?.code || undefined,
    countryId: address?.country?.alpha2code || undefined,
    region: address?.region || undefined,
  };
};

// Transform tax documents from GraphQL format to PayeeVendorTaxDocument format
const transformTaxDocuments = async (
  documents: (GetPayeeQueryDocument | null)[],
): Promise<PayeeVendorTaxDocument[]> => {
  if (!documents?.length) {
    return [];
  }

  // Filter out null values and only include tax documents
  const validTaxDocuments = documents.filter(
    (doc): doc is GetPayeeQueryDocument =>
      doc !== null && (doc.documentType === "us_tax" || doc.documentType === "non_us_tax"),
  );

  const transformedDocuments = await Promise.all(
    validTaxDocuments.map(async (doc) => {
      let file: File;

      try {
        // Fetch the file content from S3
        const response = await fetch(doc!.viewUrl || "");
        const blob = await response.blob();
        // Create File object with actual content
        file = new File([blob], doc!.name, {
          type: doc!.contentType || blob.type || "application/octet-stream",
        });
      } catch (error) {
        // Fallback to minimal File object if fetch fails
        file = new File([""], doc!.name, {
          type: doc!.contentType || "application/octet-stream",
        });
      }

      return {
        name: doc!.name,
        size: doc!.size,
        s3ObjectKey: doc!.s3ObjectKey,
        localUrl: doc!.viewUrl || "",
        contentType: doc!.contentType || undefined,
        file,
        documentType: convertToTaxDocumentType(doc!.documentType),
      };
    }),
  );

  return transformedDocuments;
};

// Transform supporting documents from GraphQL format to PayeeBaseSupportingDocument format
const transformDocumentsVendor = async (
  documents: (GetPayeeQueryPaymentPrefDocument | null)[],
): Promise<PayeeBaseSupportingDocument[]> => {
  if (!documents?.length) {
    return [];
  }

  // Filter out null values
  const validDocuments = documents.filter(
    (doc): doc is GetPayeeQueryPaymentPrefDocument => doc !== null,
  );

  const transformedDocuments = await Promise.all(
    validDocuments.map(async (doc) => {
      let file: File;

      try {
        // Fetch the file content from S3
        const response = await fetch(doc!.viewUrl || "");
        const blob = await response.blob();
        // Create File object with actual content
        file = new File([blob], doc!.name, {
          type: doc!.contentType || blob.type || "application/octet-stream",
        });
      } catch (error) {
        // Fallback to minimal File object if fetch fails
        file = new File([""], doc!.name, {
          type: doc!.contentType || "application/octet-stream",
        });
      }

      return {
        name: doc!.name,
        size: doc!.size,
        s3ObjectKey: doc!.s3ObjectKey,
        localUrl: doc!.viewUrl || "",
        contentType: doc!.contentType || undefined,
        file,
      };
    }),
  );

  return transformedDocuments;
};

// Data transformation utility to convert GraphQL payee data to PayeeData format
export const transformPayeeDataForEdit = async (
  payee: GetPayeeQueryPayee,
): Promise<PayeeData | undefined> => {
  if (!payee.paymentPref?.paymentPrefFull) {
    return undefined;
  }

  let form1099Required: PayeeData["form1099Required"] = undefined;
  if (payee.form1099Required !== null) {
    form1099Required = payee.form1099Required ? "yes" : "no";
  }

  const paymentPrefDocuments = payee.paymentPrefDocuments
    ? await transformDocumentsVendor(payee.paymentPrefDocuments)
    : undefined;
  const taxDocuments = payee.payeeDocuments
    ? await transformTaxDocuments(payee.payeeDocuments)
    : undefined;
  const result = {
    payeeId: payee.id,
    payeeName: payee.name,
    arenaId: payee.arena.id,
    arenaName: payee.arena.name,
    taxId: payee.taxId || undefined,
    taxCountryId: payee.taxIdCountry?.id || undefined,
    form1099Required,
    payeeAddress: payee.address ? transformAddress(payee.address) : undefined,
    paymentPref: payee.paymentPref?.paymentPrefFull,
    paymentPrefId: payee.paymentPref?.paymentPrefFull?.paymentPrefId || undefined,
    supportingDocuments: payee.payeeType == "vendor" ? undefined : paymentPrefDocuments,
    // Note: invoices and taxDocuments are not available in GetPayeeQuery
    // These would need to be handled separately if needed for edit workflows
    invoices: payee.payeeType == "vendor" ? paymentPrefDocuments : undefined,
    taxDocuments: payee.payeeType == "vendor" ? taxDocuments : undefined,
  };

  return result;
};

export const getPayeeStatusDisplay = (status: PayeeStatusType) => {
  switch (status) {
    case PayeeStatus.Pending:
      return PayeeDisplayStatusTypes.Pending;
    case PayeeStatus.Approved:
      return PayeeDisplayStatusTypes.Approved;
    case PayeeStatus.Rejected:
      return PayeeDisplayStatusTypes.Rejected;
    default:
      return status ? status : PayeeDisplayStatusTypes.Unknown;
  }
};
